import { useApiMutation, type ApiClient } from "@gladis/api-client";
import {
  Button,
  DecimalInput,
  Label,
  MoneyInput,
  parseEurInput,
  SelectField,
  TextField,
} from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

import { durationMonthsBetween, scheduleAnchor } from "../../index.js";
import { PROPERTIES_KEY } from "./PropertyList.js";
import type { MortgagePartDto } from "./MortgagePartList.js";

/*
 * THE CONSTANT HOLDS KEYS, NOT SENTENCES — feature 021.
 *
 * A module-scope `t("…")` is evaluated once, when the module is first imported, which freezes the
 * label in whichever language happened to be loaded at that moment. It renders correctly for whoever
 * opens the application and wrongly for whoever switches, and nothing about it looks wrong.
 *
 * These three are the same word in both languages — see the note in `en.ts` — so the bug would have
 * been invisible here in particular. That is the reason to fix the SHAPE rather than rely on the
 * values: the next person to add a fourth type will not know that.
 */
const TYPES = [
  { value: "annuity", labelKey: "prop.mortgage.annuity" },
  { value: "linear", labelKey: "prop.mortgage.linear" },
  { value: "interest_only", labelKey: "prop.mortgage.interestOnly" },
] as const;

/**
 * Add one `leningdeel` (FR-020 to FR-023).
 *
 * THERE IS NO FIELD FOR WHAT IS STILL OWED, and there is no field for a monthly payment. Both are
 * derived from what is entered here plus today's date. That absence is the feature — it is what
 * makes the figure always current without anyone maintaining it.
 *
 * A rate of 0 is accepted and is not a mistake: an interest-free family loan is a real arrangement,
 * and it is the input the annuity formula degenerates on, which the server handles as the limit.
 */
/**
 * ONE FORM FOR ADDING AND CORRECTING, because the two take identical input.
 *
 * `UpdateMortgagePartRequestSchema` is `MortgagePartInputSchema` — the same schema the create route
 * uses, not a subset of it. Every field of a part is correctable, so a second form would have been
 * this one with a different verb, and the two would have drifted the first time a field was added.
 *
 * The only differences are where it posts and what it starts with, and both are one line.
 *
 * `endDate` IS DERIVED WHEN EDITING, not stored: a part records a duration in months, and the last
 * payment is what the member reads off a statement. Recomputing it from the start date and the
 * duration is what makes the form show the same date the member originally typed.
 */
/**
 * The part's first payment date — the day its instalment leaves the account (feature 024).
 *
 * IT IS NOT THIS CONTEXT'S FACT. The date lives on the expenses side, as the schedule of the mortgage row that
 * part becomes there, and a due date is not an attribution and not a property of the loan (FR-007, FR-008). So
 * this form takes the current value and a function that saves it, exactly as `ExpenseForm` takes its
 * `destination`: it never learns where the date is kept, and the properties package still imports nothing
 * from expenses. `apps/app` is where the two meet.
 */
export interface MortgagePaymentDate {
  /** The date in force today, or null when the part is undated. */
  current: string | null;
  save: (client: ApiClient, partId: string, firstPaymentDate: string | null) => Promise<unknown>;
  invalidates: readonly (readonly unknown[])[];
}

export function MortgagePartForm({
  propertyId,
  part,
  onAdded,
  paymentDate,
}: {
  propertyId: string;
  /** Present when correcting an existing part. Absent when adding one. */
  part?: MortgagePartDto | undefined;
  onAdded?: () => void;
  /** Offers the first payment date field when given. */
  paymentDate?: MortgagePaymentDate | undefined;
}): ReactNode {
  const t = useT();
  const [name, setName] = useState(part?.name ?? "");
  const [mortgageType, setMortgageType] = useState<string>(part?.mortgageType ?? "annuity");
  const [originalPrincipal, setOriginalPrincipal] = useState(
    part ? String(part.originalPrincipal) : "",
  );
  const [interestRate, setInterestRate] = useState(part ? String(part.interestRate) : "");
  const [startDate, setStartDate] = useState(part?.startDate ?? "");
  const [endDate, setEndDate] = useState(part ? lastPaymentOf(part) : "");
  const [lender, setLender] = useState(part?.lender ?? "");
  /*
   * THE DATE AS THE FORM OPENED, and the only thing "changed" is measured against. Comparing with the live prop
   * would turn a schedule that finished loading after the dialog opened into a CLEAR: the field still empty,
   * the prop now a date, and the save sending null. Measured against the opening value it sends nothing.
   */
  const [openedWith] = useState(paymentDate?.current ?? "");
  const [firstPaymentDate, setFirstPaymentDate] = useState(openedWith);
  /*
   * THE PART A CREATE ALREADY WROTE. The part and its date are two requests to two contexts, so the second can
   * fail after the first succeeded. Submitting again must then retry only the date — posting the part a second
   * time would add a duplicate loan, which is far worse than a missing date.
   */
  const [savedPartId, setSavedPartId] = useState<string | null>(part?.id ?? null);

  const add = useApiMutation<{ mortgagePartId: string }, Record<string, unknown>>(
    (client, input) =>
      part
        ? client.patch<{ mortgagePartId: string }>(
            `/properties/${propertyId}/mortgage-parts/${part.id}`,
            input,
          )
        : client.post<{ mortgagePartId: string }>(
            `/properties/${propertyId}/mortgage-parts`,
            input,
          ),
    { invalidates: [PROPERTIES_KEY] },
  );
  const saveDate = useApiMutation<unknown, { partId: string; date: string | null }>(
    (client, { partId, date }) => paymentDate?.save(client, partId, date) ?? Promise.resolve(),
    { invalidates: paymentDate?.invalidates ?? [] },
  );
  const dateChanged = paymentDate !== undefined && firstPaymentDate !== openedWith;

  const principal = parseEurInput(originalPrincipal);
  const rate = interestRate.trim() === "" ? null : Number(interestRate.replace(",", "."));

  /*
   * THE TERM, DERIVED FROM THE TWO DATES THE STATEMENT QUOTES.
   *
   * `durationMonthsBetween` is the domain's rule and this form does not restate it: the first payment falls
   * the month after drawdown and the last falls in the end month, both counted. Recomputing that here would
   * be a second implementation of the arithmetic that decides what the household pays.
   */
  const derivedMonths =
    startDate === "" || endDate === "" ? null : durationMonthsBetween(startDate, endDate);
  const firstPayment = startDate === "" ? "" : scheduleAnchor(startDate);
  const months = derivedMonths ?? 0;
  const canSubmit =
    principal !== null &&
    rate !== null &&
    Number.isFinite(rate) &&
    rate >= 0 &&
    Number.isInteger(months) &&
    months > 0 &&
    startDate !== "";

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (principal === null || rate === null) return;

    const thenSaveDate = (partId: string): void => {
      setSavedPartId(partId);
      if (!dateChanged) {
        onAdded?.();
        return;
      }
      saveDate.mutate(
        { partId, date: firstPaymentDate === "" ? null : firstPaymentDate },
        { onSuccess: () => onAdded?.() },
      );
    };

    // A create whose date failed: the part exists, so only the date is retried.
    if (part === undefined && savedPartId !== null) {
      thenSaveDate(savedPartId);
      return;
    }

    add.mutate(
      {
        name: name.trim() === "" ? null : name.trim(),
        mortgageType,
        originalPrincipal: principal,
        interestRate: rate,
        startDate,
        durationMonths: months,
        lender: lender.trim() === "" ? null : lender.trim(),
      },
      { onSuccess: (result) => thenSaveDate(part?.id ?? result.mortgagePartId) },
    );
  }

  return (
    /*
     * NO CARD CHROME. This form is rendered inside a dialog, which is already a surface — a bordered
     * panel within a modal is a card inside a card, with two borders and doubled padding.
     *
     * A form does not know where it is rendered and should not decide that it is a card. The caller
     * does: the dialog supplies its own surface, and the one place this is used inline wraps it in a
     * `Panel` the same way every other section is wrapped.
     */
    <form onSubmit={submit} className="flex flex-col gap-4">
      {/*
       * NO HEADING AND NO DESCRIPTION. The dialog supplies both — they are its accessible name and
       * description — and repeating them here rendered the same two sentences twice, once as the
       * modal's title and once as the form's.
       */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="part-name">{t("prop.mortgage.name")}</Label>
          <TextField
            id="part-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("prop.mortgage.namePlaceholder")}
          />
        </div>

        <div>
          <Label htmlFor="part-type">{t("prop.facts.type")}</Label>
          <SelectField
            id="part-type"
            value={mortgageType}
            onChange={(event) => setMortgageType(event.target.value)}
          >
            {TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {t(type.labelKey)}
              </option>
            ))}
          </SelectField>
        </div>

        <div>
          <Label htmlFor="part-principal">{t("prop.mortgage.amountBorrowed")}</Label>
          <MoneyInput
            id="part-principal"
            value={originalPrincipal}
            onValueChange={setOriginalPrincipal}
            required
          />
        </div>

        <div>
          <Label htmlFor="part-rate">{t("prop.mortgage.interestRate")}</Label>
          <DecimalInput
            id="part-rate"
            value={interestRate}
            // `numeric(6, 3)` in the schema, so three decimals: a rate of 3,475% is real
            // and a two-decimal field would have refused the fourth keystroke in silence.
            scale={3}
            onValueChange={setInterestRate}
            placeholder="3.5"
            required
          />
        </div>

        <div>
          <Label htmlFor="part-start">{t("prop.mortgage.startDate")}</Label>
          <TextField
            id="part-start"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            required
          />
        </div>

        <div>
          {/*
            THE END DATE, WHICH IS WHAT THE STATEMENT SAYS.

            A mortgage statement reads "Looptijd leningdeel van 6-8-2025 tot 30-6-2052" and never says
            "322 months". Asking for the number invites the error that is hardest to spot: a part entered
            as 360 months instead of 322 pays €524.46 where the bank takes €561.72, and nothing about the
            figure looks wrong.

            The term is derived by `durationMonthsBetween` — the domain's rule, not this form's — and shown
            beneath so the member can see what was worked out rather than trusting it.
          */}
          <Label htmlFor="part-end">{t("prop.mortgage.lastPayment")}</Label>
          <TextField
            id="part-end"
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            required
          />
          <p className="mt-1 text-sm text-muted">
            {derivedMonths === null
              ? t("prop.mortgage.termHint")
              : `${derivedMonths} monthly payments, the first on ${firstPayment}.`}
          </p>
        </div>

        {paymentDate === undefined ? null : (
          <div className="sm:col-span-2">
            <Label htmlFor="part-first-payment">{t("prop.mortgage.paymentDate")}</Label>
            <TextField
              id="part-first-payment"
              type="date"
              value={firstPaymentDate}
              onChange={(event) => setFirstPaymentDate(event.target.value)}
            />
            <p className="mt-1 text-sm text-muted">{t("prop.mortgage.paymentDateHint")}</p>
          </div>
        )}

        <div className="sm:col-span-2">
          <Label htmlFor="part-lender">{t("prop.mortgage.lender")}</Label>
          <TextField
            id="part-lender"
            value={lender}
            onChange={(event) => setLender(event.target.value)}
            placeholder="ING"
          />
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <Button
          type="submit"
          variant="primary"
          disabled={!canSubmit || add.isPending || saveDate.isPending}
        >
          {add.isPending || saveDate.isPending ? t("common.saving") : t("prop.mortgage.addPart")}
        </Button>
        {add.isError ? (
          <span className="text-sm text-danger">{errorSentence(add.error)}</span>
        ) : saveDate.isError ? (
          <span className="text-sm text-danger">
            {t("prop.mortgage.paymentDateFailed", { reason: errorSentence(saveDate.error) })}
          </span>
        ) : null}
      </div>
    </form>
  );
}

/**
 * The last payment date, back-derived from the start date and the term.
 *
 * A part stores `durationMonths`; the form asks for the date off the statement. Editing has to show
 * what was originally entered, so the same arithmetic runs backwards — the month before the start
 * plus `durationMonths`, taken to the end of that month, which is exactly what the forward
 * derivation inverts.
 */
function lastPaymentOf(part: MortgagePartDto): string {
  const [year, month, day] = part.startDate.split("-").map(Number);
  if (!year || !month || !day) return "";
  // The last payment falls in the month `durationMonths` after the first one.
  const end = new Date(Date.UTC(year, month - 1 + part.durationMonths, 0));
  return end.toISOString().slice(0, 10);
}
